import {
    ClipboardDocumentListIcon,
    PencilIcon
} from "@heroicons/react/24/solid";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import Button from "../../components/Button";
import FilteredTable from "../../components/FilteredTable";
import { LoadingMask } from "../../components/LoadingMask";
import { PolicySellsReportPath } from "../../constants/Routes";
import { PolicyStatusLabel } from "../../enums/PolicyState";
import { RoleEnum } from "../../enums/Role";
import { Policy } from "../../models/Policies";
import {
    fetchPolicies,
    pastPage
} from "../../state/policies/actions";
import { AppDispatch } from "../../state/store";
import {
    longLocalDateFormat,
    shortDateFormat
} from "../../utils/functions";
import { PolicyFilterModal } from "./PolicyFilterModal";
import { ClientPolicyModal } from "../../components/clients/policy/ClientPolicyModal";

interface Props {}

export const Policies = (props: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const pagePolicies = useSelector((state: any) => state.policies.pagePolicies);
  const paginatedOptions = useSelector(
    (state: any) => state.policies.paginatedOptions
  );
  const isLoading = useSelector(
    (state: any) => state.policies.isLoadingPolicies
  );
  const currentUser = useSelector((state: any) => state.users.currentUser);
  const [isFilterModalOpen, setFilterModalOpen] = useState<boolean>(false);
  const dispatch = useDispatch<AppDispatch>();

  const [showPolicyModal, setShowPolicyModal] = useState<{
    show: boolean;
    update: boolean;
    policy?: Policy;
    isNewClient: boolean;
    canEditClient: boolean;
    clientPolicies: Policy[];
    isFromPolicies: boolean;
  }>({
    show: false,
    update: false,
    isNewClient: false,
    canEditClient: true,
    clientPolicies: [],
    isFromPolicies: true
  });

  const handleRowClick = (policy: any) => {
    showPolicyUpdateModal(policy);
  };

  const renderClickableCell = (value: any, row: any) => (
    <div
      onClick={() => handleRowClick(row.row.original)}
      className="w-full h-full block px-2 py-1 cursor-pointer hover:bg-gray-100"
    >
      {value}
    </div>
  );

  const columns = useMemo(() => {
    const showColumns = [
      {
        Header: t("POLICY_NUMBER"),
        accessor: "number",
        Cell: (row: any) => renderClickableCell(row.value, row),
      },
      {
        Header: t("CARRIER"),
        Cell: (row: any) => {
          const value = `${row.row.original.carrier}`;
          return renderClickableCell(value, row);
        },
      },
      {
        Header: t("CLIENT"),
        accessor: "client",
        Cell: (row: any) => renderClickableCell(row.value, row),
      },
      {
        Header: t("EFFECTIVE_START_DATE"),
        Cell: (row: any) => {
          const formattedDate = shortDateFormat(`${row.row.original.effective_start_date}`);
          return renderClickableCell(formattedDate, row);
        },
      },
      {
        Header: t("POLICY_TYPE"),
        Cell: (row: any) => {
          const type = row.row.original.policy_type != null ? `${row.row.original.policy_type}` : "-";
          return renderClickableCell(type, row);
        },
      },
      {
        Header: t("STATUS"),
        Cell: (row: any) => {
          const label = row.row.original.policy_state != null
            ? PolicyStatusLabel(row.row.original.policy_state)
            : "-";
          return renderClickableCell(label, row);
        },
      },
    ];

    const actions = {
      Header: " ",
      Cell: (row: any) => {
        return (
          <div className="flex flex-row justify-center divide-x">
            <Button
              isTerciary
              tooltip={t("UPDATE_POLICY")}
              iconOnly
              onClick={() => showPolicyUpdateModal(row.row.original)}
              classNames="mr-2"
            >
              <PencilIcon className="h-5 aspect-square text-blue" />
            </Button>
          </div>
        );
      },
    };

    if (currentUser?.role_id !== RoleEnum.AGENT) {
      return [
        ...showColumns,
        {
          Header: t("AGENT"),
          Cell: (row: any) => {
            const value = `${row.row.original.agent ?? ""}`;
            return renderClickableCell(value, row);
          },
        },
        {
          Header: t("LAST_UPDATE"),
          Cell: (row: any) => {
            const date = row.row.original.last_update != null
              ? longLocalDateFormat(`${row.row.original.last_update}`)
              : "-";
            return renderClickableCell(date, row);
          },
        },
        actions,
      ];
    }
    return [
      ...showColumns,
      {
        Header: t("LAST_UPDATE"),
        Cell: (row: any) =>{
          const date = row.row.original.last_update != null
            ? longLocalDateFormat(`${row.row.original.last_update}`)
            : "-";
            return renderClickableCell(date,row);
          }
      },
      actions,
    ];
  }, [t, currentUser]);

  const getPaginationOptions = useMemo(() => {
    return {
      page: paginatedOptions.page,
      pageSize: paginatedOptions.pageSize,
      totalPages: paginatedOptions.totalPages,
      totalCount: paginatedOptions.totalCount,
      setPage: (value: number) => {
        value < paginatedOptions.page
          ? dispatch(pastPage())
          : dispatch(
              fetchPolicies({
                page: value,
                pageSize: paginatedOptions.pageSize,
              })
            );
      },
      setPageSize: (value: number) =>
        dispatch(
          fetchPolicies({ page: paginatedOptions.page, pageSize: value })
        ),
    };
  }, [dispatch, paginatedOptions]);

  const showPolicyUpdateModal = (policy: Policy) => {
    setShowPolicyModal({
      show: true,
      update: true,
      policy: policy,
      isNewClient: false,
      canEditClient: true,
      clientPolicies: [],
      isFromPolicies: true
    })
  };

  return (
    <>
      {isLoading && <LoadingMask />}
      <ClientPolicyModal
        info={showPolicyModal}
        onClose={() => 
          setShowPolicyModal({
            show: false,
            update: false,
            policy: undefined,
            isNewClient: false,
            canEditClient: true,
            clientPolicies: [],
            isFromPolicies: true
          })
        }
        addCPolicy={() => {}}
        updateCPolicy={() => {}}
      />
      <ToastContainer progressStyle={{ background: "#D4AF37" }} />
      <FilteredTable
        columns={columns}
        data={pagePolicies}
        isPaginated
        paginationOptions={getPaginationOptions}
        hasSearchBar={false}
        secondaryActionButton={
          currentUser?.role_id !== RoleEnum.AGENT ? (
            <Button onClick={() => navigate(PolicySellsReportPath)}>
              <span className="flex items-center font-semibold pr-3">
                <div className="w-8 p-1 aspect-square mr-2">
                  <ClipboardDocumentListIcon />
                </div>
                {t("POLICY_SALES_REPORT")}
              </span>
            </Button>
          ) : (
            <div></div>
          )
        }
        /*buttonAction={<Button onClick={() => setFilterModalOpen(true)} iconOnly classNames="w-9 p-1 aspect-square mr-4 ">
                <FunnelIcon className=""></FunnelIcon></Button>}
                */
      />
      <PolicyFilterModal
        isFilterModalOpen={isFilterModalOpen}
        setFilterModalOpen={() => setFilterModalOpen(false)}
      />
    </>
  );
};
